import { useDraggable } from '@dnd-kit/core';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Briefcase, MoreHorizontal } from 'lucide-react';
import type { LeadWithRelations } from '@/types';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FollowUpIndicator } from '@/components/leads/FollowUpIndicator';
import { LEAD_STAGES, allowedMoves, stageOf, type LeadStage } from '@/components/leads/leadStages';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { cn } from '@/lib/utils';

/** "5m", "3h", "2d" — how long ago the lead came in */
function age(createdAt: string): string {
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / 60000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

const stopDrag = {
  onMouseDown: (e: React.SyntheticEvent) => e.stopPropagation(),
  onTouchStart: (e: React.SyntheticEvent) => e.stopPropagation(),
  onKeyDown: (e: React.SyntheticEvent) => e.stopPropagation(),
  onClick: (e: React.SyntheticEvent) => e.stopPropagation(),
};

interface LeadsBoardCardProps {
  lead: LeadWithRelations;
  onOpen?: (id: string) => void;
  onMove?: (lead: LeadWithRelations, to: LeadStage) => void;
  selected?: boolean;
  /** Rendered inside the DragOverlay: no drag wiring, lifted look */
  overlay?: boolean;
}

export function LeadsBoardCard({ lead, onOpen, onMove, selected, overlay }: LeadsBoardCardProps) {
  const navigate = useNavigate();
  const { formatMoney } = useCompanyLocale();
  const stage = stageOf(lead.status);
  const moves = allowedMoves(stage);

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    // The overlay copy must not register under the real card's id
    id: overlay ? `overlay:${lead.id}` : lead.id,
    data: { lead },
    disabled: overlay || moves.length === 0,
  });

  const source = lead.lead_sources?.name || lead.campaigns?.name;
  const details = [lead.customer_suburb, lead.service_types?.name].filter(Boolean).join(' · ');

  return (
    <div
      ref={overlay ? undefined : setNodeRef}
      {...(overlay ? {} : attributes)}
      {...(overlay ? {} : listeners)}
      onClick={() => onOpen?.(lead.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen?.(lead.id);
      }}
      className={cn(
        'group rounded-md border bg-card p-3 text-[13px] shadow-sm outline-none transition-colors',
        'focus-visible:ring-2 focus-visible:ring-ring',
        moves.length > 0 ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
        selected ? 'border-primary bg-primary/5' : 'hover:border-primary/40',
        isDragging && 'opacity-40',
        overlay && 'rotate-1 cursor-grabbing shadow-lg',
      )}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium leading-tight">{lead.customer_name || 'Unknown caller'}</p>
          {details && <p className="mt-0.5 truncate text-[12px] text-muted-foreground">{details}</p>}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <span className="text-[11px] text-muted-foreground tabular-nums">{lead.lead_number}</span>
          {!overlay && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild {...stopDrag}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="-mr-1 h-6 w-6 text-muted-foreground"
                  aria-label="Lead actions"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" {...stopDrag}>
                {moves.length > 0 && (
                  <>
                    <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-[.04em] text-muted-foreground">
                      Move to
                    </DropdownMenuLabel>
                    {LEAD_STAGES.filter((s) => moves.includes(s.value)).map((s) => (
                      <DropdownMenuItem key={s.value} onSelect={() => onMove?.(lead, s.value)}>
                        <span className={cn('mr-2 h-2 w-2 rounded-full', s.dotClass)} />
                        {s.label}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                  </>
                )}
                {stage === 'converted' && lead.job_id && (
                  <DropdownMenuItem onSelect={() => navigate(`/jobs/${lead.job_id}`)}>
                    <Briefcase className="mr-2 h-4 w-4" />
                    Open job
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => onOpen?.(lead.id)}>
                  <ArrowRight className="mr-2 h-4 w-4" />
                  Open lead
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {source && (
        <span className="mt-2 inline-block max-w-full truncate rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
          {source}
        </span>
      )}

      {stage === 'lost' && lead.lost_reason && (
        <p className="mt-2 truncate text-[12px] text-muted-foreground" title={lead.lost_reason}>
          {lead.lost_reason}
        </p>
      )}

      <div className="mt-2.5 flex items-center gap-2 text-[12px]">
        {lead.csr_profile?.full_name ? (
          <span className="min-w-0 truncate text-muted-foreground">{lead.csr_profile.full_name}</span>
        ) : (
          <span className="italic text-warning">Unassigned</span>
        )}
        <span className="shrink-0 text-muted-foreground tabular-nums" title={new Date(lead.created_at).toLocaleString()}>
          {age(lead.created_at)}
        </span>
        {stage === 'needs_follow_up' && <FollowUpIndicator date={lead.next_follow_up_date} variant="compact" />}
        <span className="ml-auto shrink-0 font-semibold tabular-nums">
          {lead.quoted_price ? formatMoney(lead.quoted_price) : ''}
        </span>
      </div>
    </div>
  );
}
